/**
 * Turning an Elliott analysis into a chart overlay.
 *
 * Kept out of the click handler so the shape the chart receives is a tested value rather than an
 * object literal assembled inline. `chartDataProcessor` reads `type`, `degree` and `points`; every
 * other field is for the overlay list and the journal.
 */

/**
 * One id, reused.
 *
 * A wave overlay is a statement about the chart currently loaded, so a second one is never wanted
 * — re-running the analysis or changing symbol replaces it. A fresh id per run would stack counts
 * from different symbols on one chart, each looking like a count of what is now displayed.
 */
export const ELLIOTT_OVERLAY_ID = 'elliott-wave-primary';

/**
 * The overlay for a result's primary count, or null when there is nothing to draw.
 *
 * Returns null rather than an empty overlay so the caller can leave the switch disabled instead of
 * turning it on and putting nothing on the chart.
 */
export function buildElliottOverlay(result) {
  const overlay = result?.primaryConclusion?.waveOverlay;
  const points = overlay?.points;
  if (!Array.isArray(points) || points.length < 2) return null;

  // Points whose bar index or price cannot be read are dropped here as well as in the renderer:
  // two pivots are needed for a line, and it is better to know that now than to add an overlay
  // that silently renders nothing.
  const usable = points.filter(p =>
    p && Number.isInteger(p.barIndex) && typeof p.price === 'number' && Number.isFinite(p.price));
  if (usable.length < 2) return null;

  return {
    type: 'elliottWave',
    id: ELLIOTT_OVERLAY_ID,
    degree: overlay.degree,
    points: usable,
    label: `Elliott · ${result.primaryConclusion.pattern} · ${result.primaryConclusion.position}`,
    symbol: result.symbol,
  };
}
